import React, { useState } from "react";
import { useSite } from "@/context/SiteContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send, CheckCircle2, FileText, Clock, ShieldCheck, MessageCircle, Paperclip, X } from "lucide-react";

const HORARIOS = ["Manhã (08-12)", "Tarde (12-18)", "Comercial (08-18)", "Qualquer horário"];

export default function Orcamento() {
  const { services, config, content } = useSite();
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", city: "", service: "", description: "", best_time: "" });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const attachmentsEnabled = (content?.lead_attachments_enabled ?? "true") === "true";

  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const handleFiles = async (selected) => {
    if (!selected?.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of Array.from(selected)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload-attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ url: data.url, filename: data.filename });
      }
      setFiles((curr) => [...curr, ...uploaded]);
      toast.success(`${uploaded.length} arquivo(s) anexado(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no upload");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (idx) => setFiles((curr) => curr.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.description) {
      toast.error("Preencha os campos obrigatórios.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, attachments: files.map((f) => f.url) });
      setSuccess(true);
      toast.success("Orçamento enviado! Em breve entraremos em contato.");
    } catch (err) {
      toast.error("Erro ao enviar. Tente novamente ou use o WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="page-orcamento" className="pt-32 md:pt-40 pb-20 min-h-screen bg-[#f5f7fa]">
        <div className="dc-container max-w-2xl text-center bg-white rounded-3xl p-12 shadow-lg border border-slate-100">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-500" />
          </div>
          <h1 data-testid="orcamento-success" className="font-heading text-3xl md:text-4xl font-bold text-dc-text mb-3">Orçamento enviado com sucesso!</h1>
          <p className="text-slate-600 leading-relaxed mb-8">Recebemos sua solicitação. Nossa equipe entrará em contato em até 1 hora útil para alinhar os detalhes.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href={`https://wa.me/${config?.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-7 py-3 rounded-full font-semibold">
              <MessageCircle className="w-4 h-4" /> Acelerar pelo WhatsApp
            </a>
            <a href="/" className="inline-flex items-center gap-2 border-2 border-slate-200 text-dc-text px-7 py-3 rounded-full font-semibold">Voltar para a Home</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-orcamento" className="pt-32 md:pt-40 pb-20 bg-[#f5f7fa] min-h-screen">
      <div className="dc-container">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-dc-primary">Solicite agora</span>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-dc-text mt-3 mb-5 leading-tight">Receba um orçamento personalizado em até 1 hora</h1>
            <p className="text-base text-slate-600 leading-relaxed mb-8">Preencha as informações abaixo e nossa equipe técnica preparará uma proposta sob medida para sua empresa.</p>

            <div className="space-y-4">
              <Info icon={FileText} title="Análise técnica" desc="Diagnóstico inicial gratuito para entender sua necessidade." />
              <Info icon={Clock} title="Resposta rápida" desc="Atendimento em até 1 hora útil para todas as solicitações." />
              <Info icon={ShieldCheck} title="Sigilo garantido" desc="Suas informações são protegidas conforme a LGPD." />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-slate-100 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Nome completo *"><Input data-testid="input-name" value={form.name} onChange={update("name")} placeholder="Seu nome" /></Field>
                <Field label="Empresa"><Input data-testid="input-company" value={form.company} onChange={update("company")} placeholder="Nome da empresa" /></Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Telefone / WhatsApp *"><Input data-testid="input-phone" value={form.phone} onChange={update("phone")} placeholder="(43) 99999-9999" /></Field>
                <Field label="E-mail *"><Input data-testid="input-email" type="email" value={form.email} onChange={update("email")} placeholder="seu@email.com" /></Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Cidade"><Input data-testid="input-city" value={form.city} onChange={update("city")} placeholder="Sua cidade" /></Field>
                <Field label="Serviço desejado">
                  <Select value={form.service} onValueChange={update("service")}>
                    <SelectTrigger data-testid="select-service"><SelectValue placeholder="Selecione um serviço" /></SelectTrigger>
                    <SelectContent>
                      {services.map((s) => (<SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>))}
                      <SelectItem value="Outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Melhor horário para contato">
                <Select value={form.best_time} onValueChange={update("best_time")}>
                  <SelectTrigger data-testid="select-besttime"><SelectValue placeholder="Selecione o horário" /></SelectTrigger>
                  <SelectContent>
                    {HORARIOS.map((h) => (<SelectItem key={h} value={h}>{h}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Descreva sua necessidade *">
                <Textarea data-testid="input-description" value={form.description} onChange={update("description")} rows={5} placeholder="Conte mais sobre o que sua empresa precisa..." />
              </Field>
              {attachmentsEnabled && (
                <div>
                  <Label className="text-sm font-medium text-dc-text mb-2 block">Anexar arquivos (opcional)</Label>
                  <label className="block">
                    <input
                      data-testid="input-files"
                      type="file"
                      multiple
                      accept=".png,.jpg,.jpeg,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                      className="hidden"
                      onChange={(e) => handleFiles(e.target.files)}
                    />
                    <span className={`flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 hover:border-dc-primary text-slate-600 hover:text-dc-primary py-5 rounded-xl cursor-pointer transition ${uploading ? "opacity-60 cursor-wait" : ""}`}>
                      {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : <><Paperclip className="w-4 h-4" /> Adicionar imagens, PDFs ou planilhas (até 10 MB cada)</>}
                    </span>
                  </label>
                  {files.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {files.map((f, i) => (
                        <div key={i} className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-3 py-2 text-sm">
                          <span className="text-slate-700 truncate flex-1"><Paperclip className="w-3.5 h-3.5 inline mr-1.5" />{f.filename}</span>
                          <button type="button" onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <Button data-testid="orcamento-submit" type="submit" disabled={loading} className="w-full bg-dc-primary hover:bg-dc-primary-hover text-white py-6 rounded-full font-semibold text-base shadow-lg shadow-dc-primary/25">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</> : <><Send className="w-4 h-4 mr-2" /> Enviar orçamento</>}
              </Button>
              <p className="text-xs text-center text-slate-500">Ao enviar você concorda com nossa política de privacidade (LGPD).</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-sm font-medium text-dc-text mb-2 block">{label}</Label>{children}</div>);
const Info = ({ icon: Icon, title, desc }) => (
  <div className="flex items-start gap-4 bg-white rounded-xl p-4 border border-slate-100">
    <div className="w-10 h-10 rounded-lg bg-dc-primary/10 text-dc-primary flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5" strokeWidth={1.6} /></div>
    <div><div className="font-heading font-semibold text-dc-text">{title}</div><div className="text-sm text-slate-600">{desc}</div></div>
  </div>
);
